import React from 'react';
import { Calendar, Clock, Upload, Eye, GraduationCap } from 'lucide-react';

const HomeworkCard = ({ homework, onSubmitClick, onReviewClick }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'GRADED':
      case 'Completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Late':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'In Progress':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Assigned':
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return 'No due date';
    
    const today = new Date();
    const due = new Date(dueDate);
    
    if (isNaN(due.getTime())) return 'Invalid due date';
    
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `${diffDays} days left`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-all duration-200 overflow-hidden">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                {homework.subject}
              </h3>
              <span className={`px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusColor(homework.status)}`}>
                {homework.status === 'GRADED' ? 'Completed' : homework.status || 'Assigned'}
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Calendar className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Due Date</p>
                  <p className="font-semibold text-gray-900">
                    {formatDate(homework.dueDate)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className={`font-semibold ${
                    getDaysUntilDue(homework.dueDate).includes('overdue') 
                      ? 'text-red-600' 
                      : 'text-blue-600'
                  }`}>
                    {getDaysUntilDue(homework.dueDate)}
                  </p>
                </div>
              </div>
              
              {homework.score && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <GraduationCap className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Score</p>
                    <p className="font-bold text-green-600">{homework.score}/100</p>
                  </div>
                </div>
              )}
            </div>
            
            {homework.description && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {homework.description}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 pt-4 border-t">
          <button
            onClick={() => onSubmitClick(homework)}
            disabled={homework.status === 'GRADED' || homework.status === 'Completed'}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg transition-colors shadow-sm font-medium ${
              homework.status === 'GRADED' || homework.status === 'Completed'
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <Upload className="w-4 h-4" />
            {homework.status === 'GRADED' || homework.status === 'Completed' ? 'Already Submitted' : 'Submit Work'}
          </button>
          <button
            onClick={() => onReviewClick(homework)}
            className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            <Eye className="w-4 h-4" />
            View Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomeworkCard;